import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { useI18n } from '../i18n/i18nContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { packagePacksAPI, sessionBundleAPI, userAPI } from '../services/api';
import { useAuth0 } from '@auth0/auth0-react';
import { getTranslatedField } from '../utils/translationUtils';
import {
  UserProfile,
  SessionBundle,
  Promotion,
  CheckoutItem,
  CheckoutItemType,
} from '@shared/types';
import { pushToDataLayer, clearEcommerce, formatSessionBundleItem } from '../utils/dataLayer';
import { formatPrice } from '@shared/utils';
import DiscountCode, {
  AppliedGiftCard,
  AppliedPromo,
} from 'src/components/CheckoutPage/DiscountCode';
import CheckoutForm from 'src/components/CheckoutPage/CheckoutForm';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

const CheckoutPage: React.FC = () => {
  const { itemType, itemId } = useParams<{
    itemType?: string;
    itemId?: string;
  }>();
  const { t, language } = useI18n();
  const { user, isAuthenticated } = useAuth0();
  const [checkoutItem, setCheckoutItem] = useState<CheckoutItem | null>(null);
  const [promotionDetails, setPromotionDetails] = useState<Promotion | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [appliedGiftCard, setAppliedGiftCard] = useState<AppliedGiftCard | null>(null);
  const [appliedPromotion, setAppliedPromotion] = useState<AppliedPromo | null>(null);

  const [initialSessionBundleIds, setInitialSessionBundleIds] = useState<string[]>([]);
  const [initialSubIds, setInitialSubIds] = useState<string[]>([]);
  const [baselineLoaded, setBaselineLoaded] = useState(false);
  const [promotionDiscountPercentage, setPromotionDiscountPercentage] = useState<number>(0);

  useDocumentTitle(t.checkout.title);

  const normalizedRoute = (() => {
    if (!itemType || !itemId) {
      return null;
    }

    const lowered = itemType.toLowerCase();
    if (lowered === 'package-pack') {
      return { type: 'package_pack' as CheckoutItemType, id: itemId };
    }
    if (lowered === 'subscription') {
      return { type: 'subscription' as CheckoutItemType, id: itemId };
    }
    if (lowered === 'session-bundle') {
      return { type: 'session_bundle' as CheckoutItemType, id: itemId };
    }
    return { type: 'session_bundle' as CheckoutItemType, id: itemId };
  })();

  useEffect(() => {
    fetchCheckoutItemDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemType, itemId]);

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchUserProfile();
      fetchBaseline();
    } else if (!isAuthenticated) {
      setBaselineLoaded(true);
    }
  }, [isAuthenticated, user]);

  const hasTrackedBeginCheckout = useRef(false);
  useEffect(() => {
    if (checkoutItem && !hasTrackedBeginCheckout.current) {
      hasTrackedBeginCheckout.current = true;
      clearEcommerce();
      if (checkoutItem.type === 'package_pack') {
        pushToDataLayer('begin_checkout', {
          content: { category: 'package_pack' },
          ecommerce: {
            items: [
              {
                item_id: checkoutItem.id,
                item_name: checkoutItem.name,
                item_category: 'Package Pack',
                value: checkoutItem.price,
              },
            ],
          },
        });
      } else {
        const category = checkoutItem.type === 'subscription' ? 'subscription' : 'session_bundle';
        const sessionBundleLike = {
          id: checkoutItem.id,
          name: checkoutItem.name,
          price: checkoutItem.price,
        };
        pushToDataLayer('begin_checkout', {
          content: { category },
          ecommerce: {
            items: [
              formatSessionBundleItem(
                sessionBundleLike,
                checkoutItem.type === 'subscription' ? 'Subscription' : 'Session Bundle'
              ),
            ],
          },
        });
      }
    }
  }, [checkoutItem]);

  useEffect(() => {
    if (promotionDetails && checkoutItem) {
      if (checkoutItem.type === 'subscription' && promotionDetails.subscription_discount_pct) {
        setPromotionDiscountPercentage(promotionDetails.subscription_discount_pct);
      } else if (
        checkoutItem.type === 'session_bundle' &&
        promotionDetails.session_bundle_discount_pct
      ) {
        setPromotionDiscountPercentage(promotionDetails.session_bundle_discount_pct || 0);
      } else if (
        checkoutItem.type === 'package_pack' &&
        promotionDetails.package_packs_discount_pct
      ) {
        setPromotionDiscountPercentage(promotionDetails.package_packs_discount_pct || 0);
      }
    }
  }, [promotionDetails, checkoutItem]);

  const fetchCheckoutItemDetails = async (code?: string) => {
    try {
      if (!normalizedRoute) {
        throw new Error(t.checkout.packagePackNotFound);
      }

      if (normalizedRoute.type === 'package_pack') {
        const packagePackResponse = await packagePacksAPI.getById(normalizedRoute.id, code);
        const packagePack = packagePackResponse.data as any;

        if (!packagePack?.id) {
          throw new Error(t.checkout.packagePackNotFound);
        }

        const packPrice = Number(packagePack.price) || 0;
        const packOriginalPrice =
          packagePack.original_price !== null && packagePack.original_price !== undefined
            ? Number(packagePack.original_price)
            : undefined;

        setCheckoutItem({
          id: packagePack.id,
          type: 'package_pack',
          name: getTranslatedField(packagePack, 'name', language) as string,
          price: packPrice,
          originalPrice: packOriginalPrice,
          units: Number(packagePack.package_count) || 0,
          packageId: packagePack.package_id,
        });
        setPromotionDetails((packagePack.promotionDetails as Promotion) || null);
        return;
      }

      const sessionBundlesResponse = await sessionBundleAPI.getAll(code);

      const responseData =
        (sessionBundlesResponse.data as any)?.data || sessionBundlesResponse.data;
      const allSessionBundles = Array.isArray(responseData?.sessionBundles)
        ? responseData.sessionBundles
        : Array.isArray(responseData)
          ? responseData
          : [];

      if (!Array.isArray(allSessionBundles)) {
        throw new Error('Invalid sessionBundles data format');
      }

      const selectedSessionBundle = allSessionBundles.find(
        (sessionBundle: SessionBundle) => sessionBundle.id === normalizedRoute.id
      );

      if (!selectedSessionBundle) {
        throw new Error(t.checkout.sessionBundleNotFound);
      }

      setCheckoutItem({
        id: selectedSessionBundle.id,
        type: selectedSessionBundle.type === 'subscription' ? 'subscription' : 'session_bundle',
        name: (getTranslatedField(selectedSessionBundle, 'name', language) as string) || '',
        price: selectedSessionBundle.price,
        originalPrice: selectedSessionBundle.originalPrice,
        units: selectedSessionBundle.sessions,
      });
      setPromotionDetails(responseData?.promotionDetails || null);
    } catch (err: any) {
      setError(err.message || t.checkout.loadError);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserProfile = async () => {
    try {
      const response = await userAPI.getProfile();
      setUserProfile(response.data);
    } catch (err: any) {
      console.error('Failed to fetch user profile:', err);
    }
  };

  const fetchBaseline = async () => {
    try {
      const [sessionBundleRes, subsRes] = await Promise.all([
        userAPI.getUserSessionBundles().catch(() => ({ data: [] })),
        userAPI.getSubscriptions().catch(() => ({ data: [] })),
      ]);

      const getIds = (res: any) => {
        const list = Array.isArray(res.data) ? res.data : res.data?.data || [];
        return list.map((item: any) => item.id);
      };

      setInitialSessionBundleIds(getIds(sessionBundleRes));
      setInitialSubIds(getIds(subsRes));
    } catch (err) {
      console.warn('Failed to fetch baseline for polling', err);
    } finally {
      setBaselineLoaded(true);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-gray-500">{t.common.loading}</div>
      </div>
    );
  }

  if (error || !checkoutItem) {
    return (
      <div className="container mx-auto max-w-4xl p-4 md:p-8">
        <div className="py-10 text-center">
          <div className="mb-4 text-red-600">{error || t.checkout.loadError}</div>
          <button onClick={() => window.history.back()} className="text-blue-600 hover:underline">
            {t.common.goBack}
          </button>
        </div>
      </div>
    );
  }

  const discountedPrice = checkoutItem.price;

  let giftCardAmountToApply = 0;
  if (appliedGiftCard) {
    giftCardAmountToApply = Math.min(appliedGiftCard.balance, discountedPrice);
  }

  const finalAmount = Math.max(0, discountedPrice - giftCardAmountToApply);

  return (
    <div className="container mx-auto max-w-4xl p-4 md:p-8">
      <h1 className="mb-8 text-center text-3xl font-bold text-gray-800 md:text-4xl">
        {t.checkout.title}
      </h1>

      <div className="grid gap-8 md:grid-cols-2">
        <div className="rounded-lg bg-white p-6 shadow-lg">
          <h2 className="mb-4 text-xl font-bold">{t.checkout.orderSummary}</h2>

          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold">{checkoutItem.name}</h3>
              <p className="text-gray-600">
                {checkoutItem.type === 'subscription'
                  ? t.checkout.monthlySubscription
                  : checkoutItem.type === 'package_pack'
                    ? t.pricing.package
                    : t.checkout.bulkSessionBundle}
              </p>
              <p className="text-gray-600">
                {checkoutItem.units}{' '}
                {checkoutItem.type === 'package_pack' ? t.pricing.packs : t.checkout.sessions}
              </p>
            </div>

            <div className="border-t pt-4">
              {checkoutItem.originalPrice &&
              checkoutItem.originalPrice > checkoutItem.price &&
              promotionDiscountPercentage > 0 ? (
                <>
                  <div className="mb-2 flex justify-between">
                    <span>{t.checkout.subtotal}</span>
                    <span>{formatPrice(checkoutItem.originalPrice)}</span>
                  </div>

                  <div className="mb-2 flex justify-between text-green-600">
                    <span>
                      {promotionDiscountPercentage}%{' '}
                      {(appliedPromotion
                        ? t.checkout.promotionCodeDiscount
                        : t.checkout.promotionDiscount) || 'Promotion Discount'}
                    </span>
                    <span>-{formatPrice(checkoutItem.originalPrice - checkoutItem.price)}</span>
                  </div>
                </>
              ) : null}

              {appliedGiftCard && (
                <div className="mb-2 flex justify-between text-green-600">
                  <span>{t.checkout.giftCardDiscount}</span>
                  <span>-{formatPrice(giftCardAmountToApply)}</span>
                </div>
              )}

              <div className="flex justify-between text-lg font-bold">
                <span>{t.checkout.total}</span>
                <span>{formatPrice(finalAmount)}</span>
              </div>
            </div>

            <DiscountCode
              type="giftCard"
              setDiscount={setAppliedGiftCard}
              discount={appliedGiftCard}
            />
            <DiscountCode
              type="promo"
              setDiscount={setAppliedPromotion}
              fetchDetails={fetchCheckoutItemDetails}
              discount={appliedPromotion}
            />
          </div>
        </div>

        <div className="rounded-lg bg-white p-6 shadow-lg">
          <div className="mb-2 flex items-center">
            <svg
              className="mr-2 h-5 w-5 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                clipRule="evenodd"
              />
            </svg>
            <h2 className="text-xl font-bold">{t.checkout.paymentDetails}</h2>
          </div>
          <p className="mb-4 text-sm text-gray-500">{t.checkout.securePaymentInfo}</p>

          <Elements stripe={stripePromise}>
            {baselineLoaded ? (
              <CheckoutForm
                checkoutItem={checkoutItem}
                giftCardBalance={giftCardAmountToApply}
                finalAmount={finalAmount}
                user={user}
                userProfile={userProfile}
                appliedGiftCard={appliedGiftCard}
                promotion={promotionDetails}
                initialSessionBundleIds={initialSessionBundleIds}
                initialSubIds={initialSubIds}
              />
            ) : (
              <div className="flex items-center justify-center py-8">
                <div className="text-gray-500">{t.common.loading}</div>
              </div>
            )}
          </Elements>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
